import React from 'react';
import {
  Camera,
  FileCheck,
  Mail,
  MoreHorizontal,
  Paperclip,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import type { BomItem, SupplyChainPlan } from '../../../types/supplyChain';
import { cn } from '../../../lib/utils';
import { hasDeliveryReceipt } from '../../../services/supplyChain/deliveryReceipt';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';

export interface BomItemRowActionsProps {
  item: BomItem;
  plan: SupplyChainPlan | undefined;
  onAddQuote?: (bomItemId: string) => void;
  onEmailSupplier?: (item: BomItem, supplierId: string) => void;
  onAttachBomDocument?: (item: BomItem) => void;
  onAttachAwardDocument?: (item: BomItem) => void;
  onAttachDeliveryPhoto?: (item: BomItem) => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

function iconBtnClass(disabled?: boolean): string {
  return cn(
    'inline-flex h-7 w-7 items-center justify-center rounded-sm border border-border/80',
    'text-text-secondary transition-colors',
    disabled
      ? 'cursor-not-allowed opacity-40'
      : 'hover:border-border hover:bg-muted/50 hover:text-text-primary',
  );
}

export const BomItemRowActions: React.FC<BomItemRowActionsProps> = ({
  item,
  plan,
  onAddQuote,
  onEmailSupplier,
  onAttachBomDocument,
  onAttachAwardDocument,
  onAttachDeliveryPhoto,
  onEdit,
  onDelete,
}) => {
  const shortlisted = (item.shortlistedSupplierIds ?? []).length;
  const firstSupplierId = (item.shortlistedSupplierIds ?? [])[0];
  const hasReceipt = hasDeliveryReceipt(item);

  // Live-QA 2026-06-11 (founder approved): "+ Add quote" shows on EVERY BoM
  // line whenever the handler exists — manually keying a quote must not
  // require a supplier shortlist first. The supplier-shortlisted "Request
  // quote" (email) path keeps its shortlist gate untouched.
  const canAddQuote = Boolean(onAddQuote);
  const canRequestQuote = shortlisted >= 1 && Boolean(onEmailSupplier) && Boolean(firstSupplierId);

  const menuItems: Array<{
    key: string;
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    tone?: 'default' | 'success';
    testId: string;
  }> = [];

  if (canRequestQuote && canAddQuote) {
    menuItems.push({
      key: 'request-quote',
      label: 'Request quote',
      icon: <Mail className="h-3.5 w-3.5" />,
      onClick: () => onEmailSupplier!(item, firstSupplierId!),
      testId: `scm-bom-email-supplier-${item.id}`,
    });
  }
  if (onAttachBomDocument) {
    menuItems.push({
      key: 'attach-doc',
      label: 'Attach BoM document',
      icon: <Paperclip className="h-3.5 w-3.5" />,
      onClick: () => onAttachBomDocument(item),
      testId: `scm-bom-attach-doc-${item.id}`,
    });
  }
  if (onAttachAwardDocument) {
    menuItems.push({
      key: 'award-doc',
      label: 'Attach award document',
      icon: <FileCheck className="h-3.5 w-3.5" />,
      onClick: () => onAttachAwardDocument(item),
      tone: 'success',
      testId: `scm-bom-attach-award-doc-${item.id}`,
    });
  }
  if (onAttachDeliveryPhoto) {
    menuItems.push({
      key: 'delivery-photo',
      label: hasReceipt ? 'Replace delivery photo' : 'Attach delivery photo',
      icon: <Camera className="h-3.5 w-3.5" />,
      onClick: () => onAttachDeliveryPhoto(item),
      tone: 'success',
      testId: `scm-bom-delivery-cta-${item.id}`,
    });
  }

  return (
    <div
      className="flex items-center gap-1 pt-1.5"
      data-testid={`scm-bom-actions-${item.id}`}
    >
      {canAddQuote && (
        <button
          type="button"
          onClick={() => onAddQuote!(item.id)}
          className="inline-flex h-7 items-center gap-1 rounded-sm border border-border px-2 text-[10px] font-semibold uppercase tracking-wider text-text-secondary hover:bg-muted/50 hover:text-text-primary"
          data-testid={`scm-bom-add-quote-${item.id}`}
        >
          <Plus className="h-3 w-3" aria-hidden />
          Add quote
        </button>
      )}
      {canRequestQuote && !canAddQuote && (
        <button
          type="button"
          onClick={() => onEmailSupplier!(item, firstSupplierId!)}
          className="inline-flex h-7 items-center gap-1 rounded-sm border border-primary/30 bg-primary/5 px-2 text-[10px] font-semibold uppercase tracking-wider text-primary hover:bg-primary/10"
          data-testid={`scm-bom-email-supplier-${item.id}`}
        >
          <Mail className="h-3 w-3" aria-hidden />
          Request quote
        </button>
      )}

      {menuItems.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger
            className={iconBtnClass()}
            data-testid={`scm-bom-more-${item.id}`}
            aria-label="Documents and delivery"
          >
            <MoreHorizontal className="h-3.5 w-3.5" aria-hidden />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[11rem] rounded-sm border-border font-mono">
            {menuItems.map((entry) => (
              <DropdownMenuItem
                key={entry.key}
                onClick={entry.onClick}
                className={cn(
                  'gap-2 rounded-md text-[10px] uppercase tracking-wider cursor-pointer',
                  entry.tone === 'success' && 'text-success focus:text-success',
                )}
                data-testid={entry.testId}
              >
                {entry.icon}
                {entry.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          title="Edit BoM item"
          className={iconBtnClass()}
          data-testid={`scm-bom-edit-${item.id}`}
        >
          <Pencil className="h-3.5 w-3.5" aria-hidden />
          <span className="sr-only">Edit</span>
        </button>
      )}

      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          title="Delete BoM item"
          className={cn(iconBtnClass(), 'text-danger hover:border-danger/30 hover:text-danger')}
          data-testid={`scm-bom-delete-${item.id}`}
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
          <span className="sr-only">Delete</span>
        </button>
      )}
    </div>
  );
};
